export const SUBJECT_DATA_TYPES = ["textField", "date", "email", "phone"];
export const NUMBER_DATA_TYPE = "number";
export const ASSET_SELECT_TYPES = ["logo", "photo"];

export const ASSET_DATA_TYPES = [
  ...ASSET_SELECT_TYPES,
  "signature",
  "footer_logo",
];
export const FORM_TYPES = ["subject", "template"];
export const TEXT_ANCHORS = ["start", "middle", "end"];
export const QR_TYPES = ["linear", "gradient"];

export const QR_CODE_TYPE_API = {
  ENCODED: "encoded",
  BASE45: "base45",
  CLAIM169: "claim169",
};

export const QR_CODE_TYPE_OPTIONS = [
  { value: QR_CODE_TYPE_API.ENCODED, labelKey: "qr_encoding_url" },
  { value: QR_CODE_TYPE_API.BASE45, labelKey: "qr_encoding_base45" },
  { value: QR_CODE_TYPE_API.CLAIM169, labelKey: "qr_encoding_claim169" },
];
export const QR_STYLES = [
  "square",
  "dots",
  "rounded",
  "extra-rounded",
  "classy",
  "classy-rounded",
];

export const EDITOR_KIND = {
  SUBJECT: "subject",
  QR: "qr",
  ASSET: "asset",
};

export const RESERVED_VARIABLE_KEYS = new Set(["qr-code"]);

export function isValidVariableKey(key) {
  const trimmed = String(key || "").trim();
  return /^[a-zA-Z][a-zA-Z0-9_-]*$/.test(trimmed);
}

export function validateNewVariableKey(key, existingKeys = {}) {
  const trimmed = String(key || "").trim();
  if (!trimmed) return "empty";
  if (RESERVED_VARIABLE_KEYS.has(trimmed)) return "reserved";
  if (!isValidVariableKey(trimmed)) return "invalid";
  if (Object.prototype.hasOwnProperty.call(existingKeys, trimmed)) {
    return "duplicate";
  }
  return null;
}

export function createDefaultVariableAttrs(variableKey = "") {
  return normalizeEntry({
    "data-type": "textField",
    "data-default-value": "",
    "data-description": "",
    "data-form-type": "subject",
    "data-key": variableKey,
    "data-label": variableKey,
    "data-name": variableKey,
    "data-required": false,
    "max-characters": 50,
    "text-anchor": "start",
    "number-of-lines": 1,
  });
}

function formatVariableLabel(variableKey) {
  return variableKey
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (char) => char.toUpperCase())
    .trim();
}

function isAutoSyncedName(currentName, previousKey) {
  const previous = String(previousKey || "");
  return !currentName || currentName === previous;
}

function isAutoSyncedLabel(currentLabel, previousKey) {
  const previous = String(previousKey || "");
  if (!currentLabel) return true;
  if (currentLabel === previous) return true;
  return currentLabel === formatVariableLabel(previous);
}

export function syncVariableKeyFields(attrs, variableKey) {
  const trimmed = String(variableKey || "").trim();
  const previousKey = attrs["data-key"] || "";
  const next = { ...attrs };

  next["data-key"] = trimmed;

  if (isAutoSyncedName(attrs["data-name"], previousKey)) {
    next["data-name"] = trimmed;
  }
  if (isAutoSyncedLabel(attrs["data-label"], previousKey)) {
    next["data-label"] = formatVariableLabel(trimmed);
  }

  return next;
}

export function getEditorKind(variableKey, attrs = {}) {
  const dataType = String(attrs["data-type"] || "").toLowerCase();
  if (dataType === "qr-code" || variableKey === "qr-code") {
    return EDITOR_KIND.QR;
  }
  if (ASSET_DATA_TYPES.includes(dataType)) {
    return EDITOR_KIND.ASSET;
  }
  return EDITOR_KIND.SUBJECT;
}

export function groupVariablesByKind(templateVariables = {}) {
  const groups = {
    [EDITOR_KIND.SUBJECT]: [],
    [EDITOR_KIND.QR]: [],
    [EDITOR_KIND.ASSET]: [],
  };

  Object.entries(templateVariables).forEach(([key, attrs]) => {
    const kind = getEditorKind(key, attrs);
    groups[kind].push({ key, attrs });
  });

  return groups;
}

export function coerceBoolean(value) {
  if (typeof value === "boolean") return value;
  if (value === "True" || value === "true" || value === "1") return true;
  if (value === "False" || value === "false" || value === "0") return false;
  return Boolean(value);
}

const COORDINATE_PERCENT_PATTERN = /^(-?\d+(?:\.\d+)?)\s*%$/;

export function isValidCoordinate(value) {
  if (value === undefined || value === null) return false;
  const str = String(value).trim();
  if (str === "") return false;
  if (COORDINATE_PERCENT_PATTERN.test(str)) return true;
  const parsed = Number(str);
  return Number.isFinite(parsed);
}

export function normalizeCoordinateValue(value) {
  if (!isValidCoordinate(value)) {
    return value;
  }

  const str = String(value).trim();
  const percentMatch = str.match(/^(-?\d+(?:\.\d+)?)\s*%$/);
  if (percentMatch) {
    return `${percentMatch[1]}%`;
  }

  return Number(str);
}

export function formatCoordinateForSvg(value) {
  if (!isValidCoordinate(value)) {
    return String(value ?? "");
  }

  return String(normalizeCoordinateValue(value));
}

export function hasPositionCoordinates(attrs = {}) {
  return isValidCoordinate(attrs.x) && isValidCoordinate(attrs.y);
}

export function variableConfigHasCoordinates(variableConfig = {}) {
  return Object.values(variableConfig).some((attrs) =>
    hasPositionCoordinates(attrs),
  );
}

export function hasPositioningAttributes(attrs = {}) {
  return hasPositionCoordinates(attrs) || Boolean(attrs["text-anchor"]);
}

export function variableConfigNeedsPositionPreview(variableConfig = {}) {
  return Object.values(variableConfig).some((attrs) =>
    hasPositioningAttributes(attrs),
  );
}

function normalizeEntry(attrs) {
  const normalized = { ...attrs };

  if ("data-required" in normalized) {
    normalized["data-required"] = coerceBoolean(normalized["data-required"]);
  }

  if ("max-characters" in normalized && normalized["max-characters"] !== "") {
    const parsed = Number(normalized["max-characters"]);
    if (!Number.isNaN(parsed)) {
      normalized["max-characters"] = parsed;
    }
  }

  if ("number-of-lines" in normalized && normalized["number-of-lines"] !== "") {
    const parsed = Number(normalized["number-of-lines"]);
    if (!Number.isNaN(parsed)) {
      normalized["number-of-lines"] = parsed;
    }
  }

  ["x", "y"].forEach((coordKey) => {
    const raw = normalized[coordKey];
    if (!(coordKey in normalized) || raw === undefined || raw === null) {
      return;
    }

    const trimmed = String(raw).trim();
    if (trimmed === "") {
      return;
    }

    if (isValidCoordinate(trimmed)) {
      normalized[coordKey] = normalizeCoordinateValue(trimmed);
    }
  });

  return normalized;
}

export function normalizeTemplateVariables(templateVariables = {}) {
  const normalized = {};

  Object.entries(templateVariables).forEach(([key, attrs]) => {
    normalized[key] = normalizeEntry(attrs || {});
  });

  return normalized;
}

export function cloneVariableConfig(templateVariables) {
  return normalizeTemplateVariables(
    JSON.parse(JSON.stringify(templateVariables || {})),
  );
}

export function serializeVariableConfigForApi(variableConfig) {
  const output = {};

  Object.entries(variableConfig).forEach(([key, attrs]) => {
    const serialized = { ...attrs };
    if (typeof serialized["data-required"] === "boolean") {
      serialized["data-required"] = serialized["data-required"]
        ? "True"
        : "False";
    }

    ["x", "y"].forEach((coordKey) => {
      if (!isValidCoordinate(serialized[coordKey])) {
        delete serialized[coordKey];
        return;
      }
      serialized[coordKey] = formatCoordinateForSvg(serialized[coordKey]);
    });

    output[key] = serialized;
  });

  return output;
}

export function updateVariableAttr(
  setVariableConfig,
  variableKey,
  attrKey,
  value,
) {
  setVariableConfig((prev) => ({
    ...prev,
    [variableKey]: {
      ...prev[variableKey],
      [attrKey]: value,
    },
  }));
}
